<template>
  <div>
    <b-card
      :style="`width: ${cardWidth}`"
      no-body
    >
      <template #header>
        <slot
          v-if="!forPrint"
          name="hint"
        />
        <b-card-title>Events and Timeline</b-card-title>
      </template>
      <b-card-body>
        <b-card-sub-title v-if="!error" class="pb-2">
          <template v-if="isLive">
            Past, future and hypothetical events showing cashflows and maturity. Toggle the button for what-if scenarios to test different hypothetical future evolution scenarios
          </template>
          <template v-else>
            Past events showing cashflows and maturity.
          </template>
        </b-card-sub-title>

        <b-form-group
          v-if="isLive && !forPrint"
          label="Show hypothetical future evolution"
          label-for="futureSwitch"
          label-cols="2"
          content-cols="2"
        >
          <b-form-checkbox
            id="futureSwitch"
            v-model="isFuture"
            switch
            :disabled="dataLoading"
            @change="getData"
          />
        </b-form-group>

        <div
          v-if="isFuture"
          class="my-1"
        >
          <b-row>
            <b-col
              cols="12"
              lg="12"
            >
              <div>
                <b-card-sub-title class="py-50">
                  Enter instantaneous shift and annualised drift percentage change to be applied to each underlying. The instantaneous shift moves all underlyings by the same amount today (or day one of the product if it hasnt started). The annualised drift applies a constant annual growth or decline from this point. The product events and payoff is then calculated given this hypothetical sample path.
                </b-card-sub-title>
                <div class="my-1 d-flex flex-wrap">
                  <span class="mr-1 align-self-center mt-1">Shift</span>
                  <div class="d-flex flex-column mr-2">
                    <span class="small shift-drift-input-label">Preset values</span>
                    <b-button-group>
                      <b-button
                        v-for="shift in shifts"
                        :key="shift"
                        :variant="parseFloat(parameters.shift) === shift ? 'primary' : 'outline-primary'"
                        class="shift-drift-button"
                        :disabled="dataLoading"
                        @click.prevent="parameters.shift=shift"
                      >
                        {{ shift }}%
                      </b-button>
                    </b-button-group>
                  </div>
                  <div class="d-flex flex-column">
                    <span class="small shift-drift-input-label">User defined</span>
                    <div class="shift-drift-input">
                      <b-form-input
                        v-model="parameters.shift"
                        type="text"
                        size="md"
                        @keypress="filterNumericInput"
                      />
                      <span class="shift-drift-input-suffix">%</span>
                    </div>
                  </div>
                </div>

                <div class="my-1 d-flex flex-wrap">
                  <span class="mr-1 align-self-center">Drift</span>
                  <b-button-group class="mr-2">
                    <b-button
                      v-for="drift in drifts"
                      :key="drift"
                      :variant="parseFloat(parameters.drift) === drift ? 'primary' : 'outline-primary'"
                      class="shift-drift-button"
                      :disabled="dataLoading"
                      @click.prevent="parameters.drift=drift"
                    >
                      {{ drift }}%
                    </b-button>
                  </b-button-group>
                  <div class="shift-drift-input">
                    <b-form-input
                      v-model="parameters.drift"
                      type="text"
                      size="md"
                      @keypress="filterNumericInput"
                    />
                    <span class="shift-drift-input-suffix">%</span>
                  </div>
                </div>

                <div class="py-1">
                  Hypothetical payoff: {{ maturityInfo.payoff }}, Time to maturity: {{ maturityInfo.duration }}
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <div
          v-if="!error"
          class="position-relative"
          style="min-height: 600px; height: 600px;"
        >
          <app-echart-line
            ref="eventsChart"
            :key="chartKey"
            :option-data="eventsChartOptions"
            slider-range-cursor="grab"
            style="height: 100%; width: 100%;"
          />
          <div
            v-if="isPreStrike && !isFuture"
            class="pre-strike-chart-message border rounded p-2 text-center bg-light"
          >
            Toggle the button above to show hypothetical future evolution for different what-if scenarios that you can select. This feature is available for products pre-strike or in-life
          </div>
        </div>
        <div
          v-else
          class="d-flex align-items-center justify-content-center py-3 text-muted"
        >
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
          <span>No data available</span>
        </div>
      </b-card-body>
    </b-card>

    <div class="pagebreak" />

    <b-card no-body>
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <b-card-title class="mb-0">Event Details</b-card-title>
          <div
            v-if="!forPrint && !error"
            class="d-flex align-items-center"
          >
            <span class="mr-1">Show Past Events</span>
            <b-form-checkbox
              v-model="showPastEvents"
              name="check-button"
              switch
              inline
            />
          </div>
        </div>
      </template>
      <b-card-body>
        <b-card
          v-if="!error"
          no-body
        >
          <template v-if="!forPrint">
            <div class="font-weight-bold mb-1">
              {{ eventsData.length }} results found <span v-if="eventsData.length > perPage">| Showing results {{ `from ${showing[0]} to ${showing[1]}` }}</span>
            </div>
            <b-input-group class="mb-2 input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="filter"
                placeholder="Search (date, type or event)"
                :disabled="false"
              />
            </b-input-group>
          </template>
          <b-table
            responsive
            :fields="eventsDataLiveFields"
            :items="eventsData"
            :current-page="currentPage"
            :per-page="forPrint ? eventsData.length : perPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-compare="customSortCompare"
            table-class="events-table-base"
            tbody-tr-class="events-table-row"
          >
            <template #cell(eventdate)="data">
              <span>{{ formatIsoDate(data.item.eventdate) }}</span>
            </template>
            <template #cell(eventname)="data">
              <div class="events-table-grid-container">
                <div
                  v-for="(eventName, index) in data.item.eventname"
                  :key="index"
                  :class="`events-table-grid-item ${(index + 1 < data.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                >
                  <span>{{ eventName }}</span>
                </div>
              </div>
            </template>
            <template #cell(status)="data">
              <div class="events-table-grid-container">
                <div
                  v-for="(status, index) in data.item.status"
                  :key="index"
                  :class="`events-table-grid-item ${(index + 1 < data.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                >
                  <feather-icon
                    v-if="status"
                    :icon="status"
                    :class="`text-${data.item.color[index]} mr-1`"
                    size="18"
                  />
                  <span v-if="data.item.spot[index] && data.item.level[index]">
                    {{ Math.round((data.item.level[index] / data.item.spot[index]) * 100) }}%
                  </span>
                </div>
              </div>
            </template>
            <template #cell(probability)="data">
              <div class="events-table-grid-container">
                <div
                  v-for="(probability, index) in data.item.probability"
                  :key="index"
                  :class="`events-table-grid-item ${(index + 1 < data.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                >
                  <span v-if="probability || probability === 0">
                    {{ Math.round(parseFloat(probability * 100)) }}%
                  </span>
                </div>
              </div>
            </template>
          </b-table>
          <div
            v-if="!forPrint"
            class="py-2 d-flex justify-content-between"
          >
            <div class="my-2">
              <span class="pb-1">Rows Per Page</span>
              <b-form-select
                v-model="perPage"
                :options="perPageOption"
                class="pagination-select"
              />
            </div>

            <div class="my-2 d-flex flex-column justify-content-end">
              <b-pagination
                v-model="currentPage"
                :total-rows="eventsFilterResult.length"
                :per-page="perPage"
                align="right"
                size="sm"
                class="my-0"
              />
            </div>
          </div>
          <loading-container v-if="dataLoading" />
          <no-data-container v-if="!dataLoading && (!eventsDataLive.CONTENT || eventsDataLive.CONTENT.length < 1)" />
        </b-card>
        <div
          v-else
          class="d-flex align-items-center justify-content-center py-3 text-muted"
        >
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
          <span>No data available</span>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { $themeColors } from '@themeConfig'
import {
  BRow,
  BCol,
  BCard,
  BCardTitle,
  BCardSubTitle,
  BCardBody,
  BTable,
  BFormCheckbox,
  BFormGroup,
  BButton,
  BButtonGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
} from 'bootstrap-vue'
import AppEchartLine from '@core/components/charts/echart/AppEchartLine.vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import useAppConfig from '@core/app-config/useAppConfig'
import { formatIsoDate } from '@/utils/formatters'
import renderChartToBase64 from './offscreenRender'

const debounce = require('lodash/debounce')

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BCardSubTitle,
    BCardBody,
    BTable,
    BFormCheckbox,
    BFormGroup,
    BButton,
    BButtonGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    AppEchartLine,
    LoadingContainer,
    NoDataContainer,
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  props: {
    productId: {
      type: [Number, String],
      required: true,
    },
    forPrint: {
      type: Boolean,
      default: false,
    },
    capitalData: {
      type: Object,
      default: () => ({
        SHOWDATA: {
          DATA: [],
        },
      }),
    },
    productState: {
      type: String,
      default: 'live',
    },
  },
  data() {
    return {
      chartKey: 0,
      cachedEventsImage: null,
      isLive: false,
      isFuture: false,
      futureFromDate: null,
      parameters: { shift: 0, drift: 0 },
      shifts: [-50, -25, 0, 25, 50],
      drifts: [-10, -5, 0, 5, 10],
      maturityInfo: {
        payoff: '',
        duration: '',
      },
      chartData: {
        SHOWDATA: {
          DATA: [],
        },
        EVENTSDATA: {
          X: [],
          Y: [],
          POINTS: [],
        },
        YMIN: 0,
        YMAX: 100,
      },
      dataLoading: false,
      eventsDataLive: {
        HEADER: [],
        CONTENT: [],
      },
      cardWidth: '99.9%',
      perPage: 10,
      currentPage: 1,
      perPageOption: [5, 10, 15, 20, 50, 100],
      filter: '',
      sortBy: '',
      sortDesc: false,
      showPastEvents: false,
      error: false,
    }
  },
  computed: {
    isPreStrike() {
      return this.productState === 'current' || this.productState === 'early_live'
    },
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    hasCapitalData() {
      return this.capitalData
        && this.capitalData.SHOWDATA
        && this.capitalData.SHOWDATA.DATA
        && this.capitalData.SHOWDATA.DATA.length > 0
    },
    eventsChartOptions() {
      if (!this.chartData.SHOWDATA || !this.chartData.SHOWDATA.DATA || this.chartData.SHOWDATA.DATA.length === 0) {
        return {
          animation: false,
          tooltip: {},
          legend: {},
          grid: {},
          xAxis: { type: 'time' },
          yAxis: { type: 'value' },
          series: [],
        }
      }

      const apexSeries = this.chartData.SHOWDATA.DATA
      const colors = [$themeColors.primary, $themeColors.success, $themeColors.danger,
        $themeColors.secondary, $themeColors.info, $themeColors.warning]

      // Find date range for forecast detection
      let mind = new Date('3000-12-31').getTime()
      let maxd = 0
      apexSeries.forEach(series => {
        series.data.forEach(item => {
          const d = new Date(item.x).getTime()
          mind = Math.min(mind, d)
          maxd = Math.max(maxd, d)
        })
      })

      // Convert ApexCharts format to ECharts format
      let yMin = this.chartData.YMIN
      let yMax = this.chartData.YMAX

      // Include all series data points in the range calculation
      apexSeries.forEach(series => {
        series.data.forEach(point => {
          if (point.y != null) {
            yMin = Math.min(yMin, point.y)
            yMax = Math.max(yMax, point.y)
          }
        })
      })

      // Include Y-axis annotation values in the range
      if (this.chartData.EVENTSDATA && this.chartData.EVENTSDATA.Y && this.chartData.EVENTSDATA.Y.length > 0) {
        const annotationYValues = this.chartData.EVENTSDATA.Y.map(a => a.y)
        const minAnnotation = Math.min(...annotationYValues)
        const maxAnnotation = Math.max(...annotationYValues)
        yMin = Math.min(yMin, minAnnotation)
        yMax = Math.max(yMax, maxAnnotation)
      }

      const range = yMax - yMin
      yMin -= range * 0.05
      yMax += range * 0.05

      yMin = Math.floor(yMin / 5) * 5
      yMax = Math.ceil(yMax / 5) * 5

      // Split series into historical and forecast parts if forecastDataPointscount exists
      // Find a series with enough data points to determine the forecast split timestamp

      let forecastSplitTimestamp = null

      if (this.isFuture) {
        forecastSplitTimestamp = new Date(this.futureFromDate).getTime()
      }

      const echartsSeries = []

      apexSeries.forEach((series, index) => {
        const seriesData = series.data.map(point => [point.x, point.y])
        const color = colors[index % colors.length]

        // Check if we should split this series into historical and forecast
        let shouldSplit = false
        let splitIndex = -1

        if (forecastSplitTimestamp) {
          // Find the index where this series crosses the forecast split point
          splitIndex = series.data.findIndex(point => {
            const pointX = typeof point.x === 'number' ? point.x : new Date(point.x).getTime()
            return pointX >= forecastSplitTimestamp
          })
          shouldSplit = splitIndex >= 0
        }

        if (shouldSplit) {
          const historicalData = seriesData.slice(0, splitIndex + 1)
          const forecastData = seriesData.slice(splitIndex)

          // Historical part (solid line)
          echartsSeries.push({
            name: series.name,
            type: 'line',
            data: historicalData,
            smooth: true,
            showSymbol: false,
            connectNulls: true,
            sampling: 'lttb',
            zlevel: 2,
            lineStyle: {
              width: 3,
              color,
              type: 'solid',
            },
            itemStyle: {
              color,
            },
          })

          // Forecast part (dashed line)
          echartsSeries.push({
            name: `${series.name} (Forecast)`,
            type: 'line',
            data: forecastData,
            smooth: true,
            showSymbol: false,
            connectNulls: true,
            sampling: 'lttb',
            zlevel: 2,
            lineStyle: {
              width: 3,
              color,
              type: 'dashed',
            },
            itemStyle: {
              color,
            },
            // Don't show forecast in legend
            legendHoverLink: false,
            showInLegend: false,
          })
        } else {
          console.log("Didn't split series")
          // No forecast, just add the series as-is
          echartsSeries.push({
            name: series.name,
            type: 'line',
            data: seriesData,
            smooth: true,
            showSymbol: false,
            connectNulls: true,
            sampling: 'lttb',
            zlevel: 2,
            lineStyle: {
              width: 3,
              color,
            },
            itemStyle: {
              color,
            },
          })
        }
      })

      // Add annotations to a dedicated invisible series so toggling data series via the legend
      // does not hide the markLine/markPoint annotations
      if (this.chartData.EVENTSDATA) {
        const markLineData = [
          ...(this.chartData.EVENTSDATA.X ? this.chartData.EVENTSDATA.X.map(xAnnotation => ({
            name: xAnnotation.label ? xAnnotation.label.text : '',
            xAxis: xAnnotation.x,
            lineStyle: {
              color: xAnnotation.borderColor || '#999',
              width: xAnnotation.borderWidth || 1,
              type: xAnnotation.strokeDashArray ? 'dashed' : 'solid',
            },
            label: {
              show: xAnnotation.label && xAnnotation.label.text,
              formatter: xAnnotation.label ? xAnnotation.label.text : '',
              color: '#fff',
              position: 'insideEndTop',
              backgroundColor: xAnnotation.label && xAnnotation.label.style ? xAnnotation.label.style.background : 'rgba(0,0,0,0.7)',
              padding: [4, 8],
              borderRadius: 4,
              fontSize: 11,
            },
          })) : []),
          ...(this.chartData.EVENTSDATA.Y ? (() => {
            const processedYValues = new Set()
            return this.chartData.EVENTSDATA.Y.map((yAnnotation, yIndex) => {
              const currentY = yAnnotation.y
              if (processedYValues.has(currentY)) return null
              processedYValues.add(currentY)
              const sameYAnnotations = this.chartData.EVENTSDATA.Y.filter(a => Math.abs(a.y - currentY) < 0.1)
              const labels = sameYAnnotations.map(a => (a.label ? a.label.text : '')).filter(text => text)
              let combinedLabel = ''
              if (labels.length === 1) {
                [combinedLabel] = labels
              } else if (labels.length === 2) {
                combinedLabel = `${labels[0]} & ${labels[1]}`
              } else if (labels.length > 2) {
                combinedLabel = `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`
              }
              const firstAnnotation = sameYAnnotations[0]
              return {
                name: combinedLabel,
                yAxis: currentY,
                lineStyle: {
                  color: firstAnnotation.borderColor || '#999',
                  width: firstAnnotation.borderWidth || 1,
                  type: 'dashed',
                },
                label: {
                  show: combinedLabel.length > 0,
                  formatter: combinedLabel,
                  color: '#fff',
                  position: yIndex % 2 === 0 ? 'insideEndTop' : 'insideEndBottom',
                  backgroundColor: firstAnnotation.label && firstAnnotation.label.style ? firstAnnotation.label.style.background : 'rgba(0,0,0,0.7)',
                  padding: [4, 8],
                  borderRadius: 4,
                  fontSize: 11,
                },
              }
            }).filter(item => item !== null)
          })() : []),
        ]

        const annotationSeries = {
          name: '__annotations__',
          type: 'line',
          data: [],
          silent: true,
          legendHoverLink: false,
          zlevel: 1,
          markLine: {
            symbol: 'none',
            silent: true,
            zlevel: 1,
            data: markLineData,
          },
        }

        if (this.chartData.EVENTSDATA.POINTS) {
          annotationSeries.markPoint = {
            symbol: 'circle',
            symbolSize: 8,
            zlevel: 1,
            data: this.chartData.EVENTSDATA.POINTS.map(pointAnnotation => {
              const isNearRightEdge = (maxd - pointAnnotation.x) / (maxd - mind) < 0.05
              return {
                name: pointAnnotation.label ? pointAnnotation.label.text : '',
                coord: [pointAnnotation.x, pointAnnotation.y],
                itemStyle: {
                  color: pointAnnotation.marker ? pointAnnotation.marker.fillColor : '#fff',
                  borderColor: pointAnnotation.marker ? pointAnnotation.marker.strokeColor : '#999',
                  borderWidth: pointAnnotation.marker ? pointAnnotation.marker.strokeWidth : 2,
                },
                label: {
                  show: pointAnnotation.label && pointAnnotation.label.text,
                  formatter: pointAnnotation.label ? pointAnnotation.label.text : '',
                  position: isNearRightEdge ? 'left' : 'right',
                  color: pointAnnotation.label ? pointAnnotation.label.style.color : '#999',
                  fontSize: pointAnnotation.label ? pointAnnotation.label.style.fontSize : 11,
                },
              }
            }),
          }
        }

        echartsSeries.push(annotationSeries)
      }

      // Build capital chart series if data is available
      const capitalChartSeries = []
      const capitalColors = [$themeColors.success, $themeColors.warning, $themeColors.danger]
      if (this.hasCapitalData) {
        this.capitalData.SHOWDATA.DATA.forEach((series, index) => {
          const seriesData = series.data.map(point => [point.x, point.y])
          capitalChartSeries.push({
            name: series.name,
            type: 'line',
            data: seriesData,
            smooth: true,
            showSymbol: false,
            stack: 'Total',
            areaStyle: {},
            xAxisIndex: 1,
            yAxisIndex: 1,
            lineStyle: {
              width: 3,
              color: capitalColors[index % capitalColors.length],
            },
            itemStyle: {
              color: capitalColors[index % capitalColors.length],
            },
          })
        })
      }

      // Combine all series without a spacer
      const allSeries = [...echartsSeries, ...capitalChartSeries]

      // Configure grids based on whether we have capital data
      const grids = this.hasCapitalData
        ? [
          {
            left: 70,
            right: 50,
            top: 60,
            height: '45%',
            containLabel: false,
          },
          {
            left: 70,
            right: 50,
            top: '70%',
            bottom: 70,
            containLabel: false,
          },
        ]
        : [
          {
            left: 70,
            right: 50,
            bottom: 70,
            top: 60,
            containLabel: false,
          },
        ]

      // Configure axes based on whether we have capital data
      const xAxes = this.hasCapitalData
        ? [
          {
            type: 'time',
            gridIndex: 0,
            max: this.chartData.XMAX,
            axisTicks: {
              show: true,
            },
            axisLabel: {
              fontSize: 12,
              formatter: value => this.formatDate(value),
            },
            splitLine: {
              show: false,
            },
          },
          {
            type: 'time',
            gridIndex: 1,
            max: this.chartData.XMAX,
            axisTicks: {
              show: true,
            },
            axisLabel: {
              fontSize: 12,
              formatter: value => this.formatDate(value),
            },
            splitLine: {
              show: false,
            },
          },
        ]
        : [
          {
            type: 'time',
            max: this.chartData.XMAX,
            axisTicks: {
              show: true,
            },
            axisLabel: {
              fontSize: 12,
              formatter: value => this.formatDate(value),
            },
            splitLine: {
              show: false,
            },
          },
        ]

      const yAxes = this.hasCapitalData
        ? [
          {
            type: 'value',
            gridIndex: 0,
            min: yMin,
            max: yMax,
            axisLabel: {
              fontSize: 12,
            },
            splitLine: {
              lineStyle: {
                opacity: this.splitLineOpacity,
                type: 'solid',
              },
            },
          },
          {
            type: 'value',
            gridIndex: 1,
            min: 0,
            max: 100,
            scale: false,
            axisLabel: {
              fontSize: 12,
              formatter: value => `${value.toFixed(0)}`,
            },
            splitLine: {
              lineStyle: {
                opacity: this.splitLineOpacity,
                type: 'solid',
              },
            },
            name: 'Probability (%)',
            nameLocation: 'middle',
            nameGap: 50,
          },
        ]
        : [
          {
            type: 'value',
            min: yMin,
            max: yMax,
            axisLabel: {
              fontSize: 12,
            },
            splitLine: {
              lineStyle: {
                opacity: this.splitLineOpacity,
                type: 'solid',
              },
            },
          },
        ]

      return {
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            link: [{ xAxisIndex: 'all' }],
          },
          triggerOn: 'mousemove|click',
          confine: true,
          formatter: params => {
            if (!params || params.length === 0) return ''

            // Get the x-axis position from the first param
            const currentX = new Date(params[0].axisValue || params[0].value[0]).getTime()
            const dateStr = this.formatDate(currentX)
            let result = `${dateStr}<br/>`

            // Determine which grid/chart is being hovered by checking the axisIndex
            const firstParamAxisIndex = params[0].axisIndex

            // Filter series to only those belonging to the hovered chart
            const relevantSeries = allSeries.filter(series => {
              // If xAxisIndex is defined, use it; otherwise default to 0
              const seriesAxisIndex = series.xAxisIndex !== undefined ? series.xAxisIndex : 0
              return seriesAxisIndex === firstParamAxisIndex
            })

            // Group params by series name (excluding forecasts)
            const paramsMap = new Map()
            params.forEach(param => {
              if (!param.seriesName.includes('(Forecast)')) {
                paramsMap.set(param.seriesName, param)
              }
            })

            // Build tooltip entries in series order - only for relevant series
            relevantSeries.forEach(series => {
              if (series.name.includes('(Forecast)')) return

              // Check if we have actual data from params
              if (paramsMap.has(series.name)) {
                const param = paramsMap.get(series.name)
                if (param.value && param.value[1] !== undefined) {
                  const value = param.value[1].toFixed(2)
                  result += `${param.marker} ${param.seriesName}: ${value}<br/>`
                }
                return
              }

              // Otherwise try to interpolate
              if (!series.data || series.data.length < 2) return

              let leftPoint = null
              let rightPoint = null
              for (let i = 0; i < series.data.length; i += 1) {
                const pointX = new Date(series.data[i][0]).getTime()
                if (pointX <= currentX) {
                  leftPoint = { x: pointX, y: series.data[i][1] }
                } else if (pointX > currentX && !rightPoint) {
                  rightPoint = { x: pointX, y: series.data[i][1] }
                  break
                }
              }

              if (leftPoint && rightPoint && leftPoint.x !== rightPoint.x) {
                const ratio = (currentX - leftPoint.x) / (rightPoint.x - leftPoint.x)
                const interpolatedY = leftPoint.y + ratio * (rightPoint.y - leftPoint.y)
                const color = series.itemStyle?.color || '#999'
                // Manually create a tooltip entry for interpolated values
                result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span> ${series.name}: ${interpolatedY.toFixed(2)}<br/>`
              }
            })

            return result
          },
        },
        legend: (() => {
          const toIconItem = s => ({
            name: s.name,
            icon: 'circle',
            itemStyle: { color: s.itemStyle.color },
          })
          return this.hasCapitalData
            ? [
              {
                show: true,
                top: 0,
                left: 0,
                itemGap: 20,
                data: echartsSeries
                  .filter(s => !s.name.includes('(Forecast)') && s.name !== '__annotations__')
                  .map(toIconItem),
              },
              {
                show: true,
                top: '61%',
                left: 0,
                itemGap: 20,
                data: capitalChartSeries.map(toIconItem),
              },
            ]
            : {
              show: true,
              top: 0,
              left: 0,
              itemGap: 20,
              data: echartsSeries
                .filter(s => !s.name.includes('(Forecast)') && s.name !== '__annotations__')
                .map(toIconItem),
            }
        })(),
        grid: grids,
        xAxis: xAxes,
        yAxis: yAxes,
        series: allSeries,
        dataZoom: this.hasCapitalData
          ? [
            {
              type: 'inside',
              zoomLock: true, // Keep page scrolling available; use the slider to zoom.
              xAxisIndex: [0, 1],
              start: 0,
              end: 100,
            },
            {
              type: 'slider',
              xAxisIndex: [0, 1],
              start: 0,
              end: 100,
              bottom: 10,
              height: 20,
            },
          ]
          : [
            {
              type: 'inside',
              zoomLock: true, // Keep page scrolling available; use the slider to zoom.
              start: 0,
              end: 100,
            },
            {
              type: 'slider',
              start: 0,
              end: 100,
              bottom: 10,
              height: 20,
            },
          ],
      }
    },
    eventsFilterResult() {
      let eventsData = cloneDeep(this.eventsDataLive.CONTENT)
      if (!this.showPastEvents) {
        eventsData = eventsData.filter(event => event.horizon !== 'Past')
      }

      if (this.filter && this.filter.length > 0) {
        const filteredByHorizon = eventsData.filter(item => (item.horizon.toLowerCase().search(this.filter.toLowerCase()) !== -1)
          || (item.eventdate.toLowerCase().search(this.filter.toLowerCase()) !== -1))

        eventsData.forEach((ed, i) => {
          // Check if eventData exists inside filteredByHorizon
          if (filteredByHorizon.find(item => isEqual(ed, item))) {
            return
          }

          let matchingRows = []

          // Filter by eventname
          ed.eventname.forEach((name, index) => {
            if (name.toLowerCase().search(this.filter) !== -1) {
              matchingRows.push(index)
            }
          })

          // Get unique matchingRows
          matchingRows = matchingRows.filter((value, index, self) => self.indexOf(value) === index)

          if (matchingRows.length > 0) {
            // Filter array columns
            eventsData[i].eventname = ed.eventname.filter((value, index) => typeof (matchingRows.find(element => element === index)) !== 'undefined')
            if (eventsData[i].probability) eventsData[i].probability = ed.probability.filter((value, index) => typeof (matchingRows.find(element => element === index)) !== 'undefined')
            if (eventsData[i].status) eventsData[i].status = ed.status.filter((value, index) => typeof (matchingRows.find(element => element === index)) !== 'undefined')
            if (eventsData[i].color) eventsData[i].color = ed.color.filter((value, index) => typeof (matchingRows.find(element => element === index)) !== 'undefined')
            if (eventsData[i].spot) eventsData[i].spot = ed.spot.filter((value, index) => typeof (matchingRows.find(element => element === index)) !== 'undefined')
            if (eventsData[i].level) eventsData[i].level = ed.level.filter((value, index) => typeof (matchingRows.find(element => element === index)) !== 'undefined')
          }
        })

        // Custom search for eventname and probability
        return eventsData.filter(item => (item.eventname.toString().toLowerCase().search(this.filter.toLowerCase()) !== -1)
          || (item.horizon.toLowerCase().search(this.filter.toLowerCase()) !== -1)
          || (item.eventdate.toLowerCase().search(this.filter.toLowerCase()) !== -1))
      }
      return eventsData
    },
    eventsDataLiveFields() {
      return this.eventsDataLive.HEADER.map(({ key, label }) => ({
        key,
        label,
        sortable: key === 'eventdate',
        tdClass: key === 'eventdate' || key === 'horizon' ? '' : 'p-0',
      }))
    },
    eventsData() {
      if (this.sortBy === 'probability') {
        return this.sortContent(this.eventsFilterResult, 'probability', this.sortDesc)
      }
      if (this.sortBy === 'eventname') {
        return this.sortContent(this.eventsFilterResult, 'eventname', this.sortDesc)
      }
      return this.eventsFilterResult
    },
    showing() {
      const start = (this.perPage * this.currentPage) - this.perPage + 1
      const end = (this.perPage * this.currentPage) > this.eventsData.length ? this.eventsData.length : (this.perPage * this.currentPage)
      return [start, end]
    },
  },
  watch: {
    'parameters.shift': function shiftWatcher() {
      this.getData()
    },
    'parameters.drift': function driftWatcher() {
      this.getData()
    },
    dataLoading(val) {
      this.$emit('loading-changed', val)
    },
    eventsChartOptions: {
      handler(option) {
        if (option && option.series && option.series.length > 0) {
          this.cachedEventsImage = this.renderEventsChartToBase64(option)
        }
      },
      deep: true,
    },
    chartKey() {
      this.$nextTick(this.bindLegendSync)
    },
  },
  mounted() {
    // Work around a bug where annotation text and label can be misaligned,
    // by slightly delaying first call to getData
    this.$emit('loading-changed', true)
    setTimeout(this.getData, 500)

    // Check if it's for print or matured and show past events
    if (this.forPrint || this.productState === 'matured') {
      this.showPastEvents = true
    }
  },
  activated() {
    this.cardWidth = '99.9%'

    setTimeout(() => {
      this.cardWidth = '100%'
    }, 100)
  },
  methods: {
    bindLegendSync() {
      const chartRef = this.$refs.eventsChart
      if (!chartRef) return
      const instance = chartRef.getChartInstance()
      if (!instance) return
      instance.off('legendselectchanged')
      instance.on('legendselectchanged', ({ name, selected }) => {
        const forecastName = `${name} (Forecast)`
        const hasForecast = this.eventsChartOptions.series
          && this.eventsChartOptions.series.some(s => s.name === forecastName)
        if (!hasForecast) return
        if (selected[name]) {
          instance.dispatchAction({ type: 'legendSelect', name: forecastName })
        } else {
          instance.dispatchAction({ type: 'legendUnSelect', name: forecastName })
        }
      })
    },
    collectData() {
      return { eventsData: this.eventsDataLive }
    },
    renderEventsChartToBase64(option) {
      const height = this.hasCapitalData ? 700 : 450
      // When capital data is present, spread the two grids further apart
      const noSliderZoom = (option.dataZoom || [])
        .filter(z => z.type !== 'slider')
      const adjustedOption = this.hasCapitalData
        ? {
          ...option,
          grid: [
            {
              left: 70, right: 50, top: 60, height: '40%', containLabel: false,
            },
            {
              left: 70, right: 50, top: '59%', bottom: 70, containLabel: false,
            },
          ],
          legend: option.legend.map((l, i) => (i === 1 ? { ...l, top: '52%' } : l)),
          dataZoom: noSliderZoom,
        }
        : { ...option, dataZoom: noSliderZoom }
      return renderChartToBase64(adjustedOption, 800, height)
    },
    collectCharts() {
      if (this.cachedEventsImage) return { lcEventsChart: this.cachedEventsImage }
      // Fallback: render synchronously from current chart options
      const option = this.eventsChartOptions
      if (option && option.series && option.series.length > 0) {
        const b64 = this.renderEventsChartToBase64(option)
        if (b64) return { lcEventsChart: b64 }
      }
      return {}
    },
    formatIsoDate,
    isValidNumber(val) {
      const str = String(val)
      return str !== '' && str !== '-' && !str.endsWith('.') && !Number.isNaN(parseFloat(str))
    },
    getData: debounce(function getData() {
      // Validate parameters before making API call
      if (!this.isValidNumber(this.parameters.shift) || !this.isValidNumber(this.parameters.drift)) {
        return
      }

      this.dataLoading = true
      this.error = false

      this.chartData = {
        SHOWDATA: {
          DATA: [],
        },
        EVENTSDATA: {
          X: [],
          Y: [],
          POINTS: [],
        },
      }
      this.eventsDataLive.CONTENT = []
      this.maturityInfo = {
        payoff: '',
        duration: '',
      }

      this.$http
        .get(`premium.cfc?method=getProductResultsEvents&productid=${this.productId}`
          + `&isfuture=${this.isFuture}&pcshift=${this.parameters.shift}&pcdrift=${this.parameters.drift}`)
        .then(response => {
          this.chartData = response.data.DATA.EVENTSCHART
          this.maturityInfo = response.data.DATA.MATURITYINFO
          this.isLive = response.data.DATA.EVENTSCHART.ISLIVE
          this.eventsDataLive = response.data.DATA.EVENTS
          if (this.isFuture) {
            this.futureFromDate = response.data.DATA.FUTUREFROMDATE
          }

          this.$emit('events-data-loaded', this.eventsDataLive)

          // Increment chartKey to force chart re-render
          this.chartKey += 1
        })
        .catch(() => {
          this.error = true
        })
        .then(() => {
          this.dataLoading = false
        })
    }, 500),
    formatDate(date) {
      return dayjs(date).format('YYYY-MM-DD')
    },
    filterNumericInput(event) {
      const char = String.fromCharCode(event.which || event.keyCode)
      // Allow digits, minus sign, and decimal point
      if (!/[\d.-]/.test(char)) {
        event.preventDefault()
      }
    },
    customSortCompare(aRow, bRow, key) {
      const a = aRow[key][0]
      const b = bRow[key][0]

      if (key === 'probability') {
        if (typeof a === 'number' && typeof b === 'number') {
          // eslint-disable-next-line no-nested-ternary
          return a < b ? -1 : a > b ? 1 : 0
        } if (typeof a === 'number' && typeof b !== 'number') {
          return 1
        }
        return -1
      }

      if (key === 'eventname') {
        // eslint-disable-next-line no-nested-ternary
        return a < b ? -1 : a > b ? 1 : 0
      }

      return null
    },
    sortContent(eventsData, sortBy, sortDesc) {
      const sortedContent = cloneDeep(eventsData)

      const tempSorted = sortedContent.map(data => {
        let sortables = []
        data[sortBy].forEach((p, index) => {
          sortables.push({
            probability: data.probability[index],
            color: data.color[index],
            eventname: data.eventname[index],
            level: data.level[index],
            spot: data.level[index],
            status: data.status[index],
          })
        })

        // Sort array of sortable objects based on the sortBy
        if (sortBy === 'eventname') {
          sortables = sortables.sort((a, b) => {
            if (a.eventname < b.eventname) {
              return sortDesc ? 1 : -1
            }
            if (a.eventname > b.eventname) {
              return sortDesc ? -1 : 1
            }
            return 0
          })
        } else {
          sortables = sortables.sort((a, b) => (sortDesc ? b[sortBy] - a[sortBy] : a[sortBy] - b[sortBy]))
        }
        return sortables
      })

      tempSorted.forEach((data, index) => {
        sortedContent[index].probability = data.map(d => d.probability)
        sortedContent[index].color = data.map(d => d.color)
        sortedContent[index].eventname = data.map(d => d.eventname)
        sortedContent[index].level = data.map(d => d.level)
        sortedContent[index].spot = data.map(d => d.spot)
        sortedContent[index].status = data.map(d => d.status)
      })
      return sortedContent
    },
  },
}

</script>

<style lang="scss" scoped>
@import '@core/scss/core.scss';

.pre-strike-chart-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 2rem);
  max-width: 600px;
}

.shift-drift-button {
  width: 80px;
}

.shift-drift-input {
  width: 90px;
  position: relative;
  display: inline-block;

  input {
    padding-right: 25px;
    width: 100%;
    border-color: $primary !important;

    &:focus {
      border-color: $primary !important;
    }
  }
}

.shift-drift-input-suffix {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: inherit;
}

.shift-drift-input-label {
  margin: 0 0 4px 4px;
}
</style>
